import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PageLoaderService {
  private readonly minVisibleMs = 300;
  private pendingHttpRequests = 0;
  private pendingNavigations = 0;
  private shownAtMs = 0;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly visibleSubject = new BehaviorSubject<boolean>(false);

  readonly visible$ = this.visibleSubject.asObservable();

  beginHttpRequest(): void {
    this.pendingHttpRequests += 1;
    this.refreshVisibility();
  }

  endHttpRequest(): void {
    this.pendingHttpRequests = Math.max(0, this.pendingHttpRequests - 1);
    this.refreshVisibility();
  }

  beginNavigation(): void {
    this.pendingNavigations += 1;
    this.refreshVisibility();
  }

  endNavigation(): void {
    this.pendingNavigations = Math.max(0, this.pendingNavigations - 1);
    this.refreshVisibility();
  }

  private refreshVisibility(): void {
    const shouldBeVisible =
      this.pendingHttpRequests > 0 || this.pendingNavigations > 0;

    if (shouldBeVisible) {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }

      if (!this.visibleSubject.value) {
        this.shownAtMs = Date.now();
        this.visibleSubject.next(true);
      }
      return;
    }

    if (!this.visibleSubject.value) {
      return;
    }

    const elapsedMs = Date.now() - this.shownAtMs;
    const remainingMs = this.minVisibleMs - elapsedMs;

    if (remainingMs <= 0) {
      this.visibleSubject.next(false);
      return;
    }

    if (this.hideTimer) {
      return;
    }

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (this.pendingHttpRequests === 0 && this.pendingNavigations === 0) {
        this.visibleSubject.next(false);
      }
    }, remainingMs);
  }
}
